import React, { useState } from 'react';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Leaf, Sprout, Flower, Shovel, HeartHandshake } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../../lib/errors';
const SERVICES = [
    { icon: Leaf, title: 'Landscaping Services', desc: 'Design and installation of stunning gardens for homes and villas.', points: ['Site survey & concept design', 'Plant selection & sourcing', 'Installation & handover'] },
    { icon: Sprout, title: 'Terrace Garden Setup', desc: 'Transform your terrace into a lush urban oasis.', points: ['Load & waterproofing check', 'Modular planters', 'Vegetable / ornamental themes'] },
    { icon: Flower, title: 'Vertical Garden Setup', desc: 'Living walls that add character and clean air.', points: ['Modular felt / pocket systems', 'Auto-drip irrigation', 'Species curation'] },
    { icon: Shovel, title: 'Garden Maintenance', desc: 'Weekly, monthly and quarterly care plans.', points: ['Pruning, feeding & repotting', 'Pest & disease control', 'Seasonal replacements'] },
    { icon: HeartHandshake, title: 'Corporate Landscaping', desc: 'Green offices, healthier teams and clients.', points: ['Office greenery packages', 'AMC & maintenance', 'Custom branding planters'] },
    { icon: Sprout, title: 'Drip Irrigation Solutions', desc: 'Smart watering. Save water and effort.', points: ['System design', 'Timer & controller setup', 'Retrofits for existing gardens'] },
];

export default function Services() {
    const [form, setForm] = useState({ name: '', mobile: '', email: '', service_type: SERVICES[0].title, location: '', message: '', budget: '' });
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.mobile || !form.service_type) { toast.error('Please fill required fields'); return; }
        setSending(true);
        try {
            await api.post('/service-enquiries', form);
            toast.success('Thanks! Our team will contact you within 24 hours.');
            setForm({ name: '', mobile: '', email: '', service_type: SERVICES[0].title, location: '', message: '', budget: '' });
        } catch (e) { toast.error(errMsg(e, 'Failed to send')); }
        finally { setSending(false); }
    };

    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />
            <section className="login-hero-bg text-white">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-14">
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Landscaping & Garden Services</h1>
                    <p className="text-white/85 mt-2 max-w-2xl">From concept to lifelong maintenance — Dharmik Gardens takes care of every green project.</p>
                </div>
            </section>
            <section className="py-12">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
                        {SERVICES.map(s => (
                            <Card key={s.title} className="p-6 border">
                                <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'hsl(142 76% 92%)' }}>
                                    <s.icon className="h-5 w-5" style={{ color: 'hsl(142 71% 30%)' }} />
                                </div>
                                <div className="font-semibold text-lg">{s.title}</div>
                                <p className="text-sm text-neutral-600 mt-1">{s.desc}</p>
                                <ul className="mt-3 space-y-1 text-sm">
                                    {s.points.map(p => <li key={p} className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2" style={{ background: 'hsl(142 71% 45%)' }} /><span className="text-neutral-700">{p}</span></li>)}
                                </ul>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>
            <section className="py-12 bg-neutral-50">
                <div className="max-w-[900px] mx-auto px-4 sm:px-6">
                    <Card className="p-6 sm:p-8">
                        <h2 className="text-2xl font-bold tracking-tight">Request a Consultation</h2>
                        <p className="text-neutral-600 mt-1 mb-5">Share your requirement and our team will get back to you within 24 hours.</p>
                        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="service-enquiry-form">
                            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="srv-name" /></div>
                            <div><Label>Mobile *</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="srv-mobile" /></div>
                            <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                            <div>
                                <Label>Service *</Label>
                                <select value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" data-testid="srv-service">
                                    {SERVICES.map(s => <option key={s.title} value={s.title}>{s.title}</option>)}
                                </select>
                            </div>
                            <div className="sm:col-span-2"><Label>Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="City, State" /></div>
                            <div className="sm:col-span-2"><Label>Budget (approx)</Label><Input value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} placeholder="e.g. ₹1,00,000" /></div>
                            <div className="sm:col-span-2"><Label>Details</Label><Textarea rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Describe your space, requirements, timeline" /></div>
                            <Button type="submit" disabled={sending} className="sm:col-span-2" data-testid="srv-submit" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>{sending ? 'Sending...' : 'Submit Enquiry'}</Button>
                        </form>
                    </Card>
                </div>
            </section>
            <CustomerFooter />
        </div>
    );
}
